import { keyBy } from "lodash";
import { createAssign } from "robodux";
import { call, latest, put, select, spawn } from "saga-query";

import { api, thunks } from "@app/state/apis/api";
import { isErr } from "@app/state/helpers/basic";
import { setDirInfoData, setDirTypes } from "@app/state/units/directories";
import { setGroupData } from "@app/state/units/groups";
import {
  addNewUserMember,
  patchUsersData,
  selectGroupDataOfSelectedUser,
  selectHomeDataOfSelectedUser,
  setUsersData,
} from "@app/state/units/users";

import { appNotify } from "./notify";

import type { ApiCtx } from "saga-query";
import type { IAdminGroupUser } from "@app/types";
// dummy repo, for registering the sagas //
type TAdmin = "su" | "admin" | "no" | any; // << reveiw this ts bug? //
const ADMIN_REPO = "admin";
export const adminRepo = createAssign<TAdmin>({
  name: ADMIN_REPO,
  initialState: "no",
});

export const adminsClear = thunks.create("adminsClear", function* (ctx, next) {
  yield* put(adminRepo.actions.set("no"));
  yield* next();
});
export const adminsInitialize = thunks.create(
  "adminsInitialize",
  function* (ctx, next) {
    yield* put(adminRepo.actions.set("su"));
    yield* call(suGroups.run, suGroups());
    yield* call(suUsers.run, suUsers());
    yield* call(suDirInfo.run, suDirInfo());
    yield* call(suDirData.run, suDirData());
    yield* next();
  }
);
export const suGroups = api.get(
  "/suGroups",
  function* (ctx: ApiCtx<any, any>, next) {
    ctx.cache = true;
    ctx.key = "suGroups";
    ctx.request = ctx.req({
      url: `/su/groups`,
      credentials: "include",
    });
    yield* next();
    const { data } = yield ctx.json;
    const groups = keyBy(data, "GROUPID");
    yield* put(setGroupData(groups));
  }
);

const joinOUserRights = (jgaprigths: string, insider: string): string => {
  // console.log('insider', insider)
  // console.log('jgaprigths', jgaprigths)

  if (!jgaprigths) jgaprigths = "-";
  if (!insider) insider = "-";

  //ensure start with - and end with - no spaces, no duplicates
  if (!jgaprigths.startsWith("-")) jgaprigths = `-${jgaprigths}`;
  if (!jgaprigths.endsWith("-")) jgaprigths = `${jgaprigths}-`;

  if (!insider.startsWith("-")) insider = `-${insider}`;
  if (!insider.endsWith("-")) insider = `${insider}-`;
  const norm = `${jgaprigths}${insider}`
    .split("-")
    .filter((r) => r)
    .join("-");
  return `-${norm}-`;
};

export const suUsers = api.get(
  "/suUsers",
  function* (ctx: ApiCtx<any, any>, next) {
    ctx.request = ctx.req({
      url: `/su/users`,
      credentials: "include",
    });
    yield* next();
    const { data } = yield ctx.json;
    const groupByGEm = data
      .map((g: IAdminGroupUser) => ({
        ...g,
        // the following are derrived in the selector:
        // _OUSER_RIGHTS_JGAP: splitOUserRigts(g.OUSER_RIGHTS)[0],
        // _OUSER_RIGHTS_INSIDER: splitOUserRigts(g.OUSER_RIGHTS)[1],
        GROUPID_EMAIL: `${g.GROUPID || "~"}/${g.OUSER_EMAIL}`,
      }))
      .reduce((acc: any, g: IAdminGroupUser) => {
        acc[g.GROUPID_EMAIL] = g;
        return acc;
      }, {});
    yield* put(setUsersData(groupByGEm));
  }
);

export const upsertGroupMember = api.post<IAdminGroupUser>(
  "/admin/updateGroupMember",
  function* (ctx: ApiCtx<any, any>, next) {
    const user = ctx.payload;
    const {
      _OUSER_RIGHTS_INSIDER = "",
      _OUSER_RIGHTS_JGAP = "",
      ...otherUserData
    } = user;

    const patchedUserData = {
      ...otherUserData,
      OUSER_RIGHTS: joinOUserRights(_OUSER_RIGHTS_JGAP, _OUSER_RIGHTS_INSIDER),
    };

    const { GROUPID_EMAIL } = patchedUserData;
    const method = GROUPID_EMAIL.startsWith("-1/") ? "POST" : "PATCH";

    ctx.request = ctx.req({
      method,
      url: `/su/users`,
      credentials: "include",
      body: JSON.stringify(patchedUserData),
    });

    yield* next();

    const response = ctx.json;
    if (!response?.data) {
      console.error("updateUser failed: No data", response);
      yield* spawn(
        appNotify.run,
        appNotify({
          type: "error",
          message: "updateUser failed: No data",
          duration: 1000,
        })
      );
      return;
    }

    const [{ AGMID = null } = {}] = response.data;
    if (!AGMID || AGMID < 0) {
      console.error("errorlevel", AGMID);
      yield* spawn(
        appNotify.run,
        appNotify({
          type: "error",
          message: `firebird procedure errorlevel: ${AGMID}`,
          duration: 1000,
        })
      );
      return;
    }

    yield* spawn(
      appNotify.run,
      appNotify({
        type: "success",
        message: "ok",
        duration: 1000,
        position: "bottom",
      })
    );
    if (method === "POST") {
      const newKey = `${patchedUserData.GROUPID}/${patchedUserData.OUSER_EMAIL}`;
      yield* put(
        addNewUserMember({
          [newKey]: { ...patchedUserData, GROUPID_EMAIL: newKey },
        })
      );
    }
    if (method === "PATCH") {
      yield* put(
        patchUsersData({ [patchedUserData.GROUPID_EMAIL]: patchedUserData })
      );
    }
    yield* call(suUsers.run, suUsers());
  }
);

export const suDirInfo = api.get(
  "/suDirInfo",
  function* (ctx: ApiCtx<any, any>, next) {
    ctx.request = ctx.req({
      url: `/su/dirinfo`,
      credentials: "include",
    });
    yield* next();
    const { data } = yield ctx.json;
    yield* put(setDirTypes(data));
  }
);

export const suDirData = api.get(
  "/suDirData",
  function* (ctx: ApiCtx<any, any>, next) {
    ctx.request = ctx.req({
      url: `/su/dirinfo/all-available`,
      credentials: "include",
    });
    yield* next();
    const { data } = yield ctx.json;
    yield* put(setDirInfoData(data));
  }
);

type TRoleAndVerification = {
  email: string;
  jgapRoles: string[];
  jgapVerified: boolean;
};
export const readUserRoles = api.post<{ id: string }>(
  "/readUserRoles",
  { saga: latest },
  function* (ctx: ApiCtx<any, any>, next) {
    ctx.request = ctx.req({
      method: "POST",
      url: `/su/users/roles`,
      credentials: "include",
      body: JSON.stringify(ctx.payload),
    });
    yield* next();
    const { data } = yield ctx.json;
    if (isErr(data)) {
      console.error(data);
      return;
    }
    const { email, jgapRoles, jgapVerified } =
      data.value as TRoleAndVerification;

    if (!email || email !== ctx.payload.id) {
      console.error("no email", data);
      return;
    }
    const currentUserData = yield* select((s) =>
      selectHomeDataOfSelectedUser(s, email)
    );
    yield* put(
      patchUsersData({
        [currentUserData.GROUPID_EMAIL]: {
          ...currentUserData,
          _roles: jgapRoles,
          _verified: jgapVerified,
        },
      })
    );
  }
);

//cannot use this api because we use to edit that data in the javascript layer no react
//so, we fetch the data in the component
export const getInsiderSyncAmaxavo = api.post<IAdminGroupUser>(
  "/getInsiderSyncAmaxavo",
  function* (ctx: ApiCtx<any, any>, next) {
    const { GROUPID_EMAIL, PERSEED } = ctx.payload;
    ctx.request = ctx.req({
      method: "POST",
      url: `/su/users/insidersync`,
      credentials: "include",
      body: JSON.stringify({ geml: GROUPID_EMAIL }),
    });
    yield* next();
    const { data } = yield ctx.json;
    console.log("data", data);
    if (isErr(data)) {
      console.error(data);
      return;
    }
    const currentUserData = yield* select((s) =>
      selectGroupDataOfSelectedUser(s, { GROUPID_EMAIL })
    );
    if (currentUserData?.GROUPID_EMAIL !== GROUPID_EMAIL) {
      console.error("no email", data);
      return;
    }
    const [{ OAMAID = null } = {}] = data.value;
    yield* put(
      patchUsersData({
        [currentUserData.GROUPID_EMAIL]: {
          ...currentUserData,
          AMAXAVOID: OAMAID,
        },
      })
    );
    // THEN UPDATE THE STUFFF //
  }
);

/* 
/login --> my login with my groups
/admin [groupinfo, users, licence stuff] -> the groups I admin
/swsadmin -> assign admins to groups. , create groups so on

*/
